import { useState } from "react";
import { IconDownload } from "@tabler/icons-react";
import Card from "../UI/Card";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Title from "../UI/Title";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { useToast } from "../UI/Toast/useToast";
import MoisPicker, { AnneePicker } from "./MoisPicker";
import {
  useExportsComptables,
  type ExportComptable,
} from "../../hooks/use-compta-pro";
import { periodeCourante, type Periode } from "./comptaUtils";
import styles from "./Compta.module.css";

interface ExportDef {
  type: ExportComptable;
  titre: string;
  description: string;
  parAnnee?: boolean;
  payload?: Record<string, unknown>;
}

const EXPORTS: ExportDef[] = [
  {
    type: "balance",
    titre: "Balance générale",
    description:
      "Tous les comptes avec totaux débit/crédit et soldes, cumulés jusqu'au mois choisi (Excel).",
  },
  {
    type: "balance",
    titre: "Balance auxiliaire",
    description:
      "Les comptes de tiers uniquement : locataires, mandants et dépôts de garantie (Excel).",
    payload: { auxiliaire: true },
  },
  {
    type: "journaux",
    titre: "Journaux",
    description:
      "Toutes les écritures ligne à ligne (BQ, VT, OD, AN) sur la plage, avec numéros et pièces (Excel).",
  },
  {
    type: "pointe",
    titre: "Contrôle des fonds détenus",
    description:
      "La pointe du garant : soldes bancaires vs comptables, ventilation des fonds par mandant (Excel).",
  },
  {
    type: "pointe-tresorerie",
    titre: "Pointe de trésorerie",
    description:
      "Déclaration de pointe pour le garant : maximum des fonds détenus sur l'exercice, ventilation au jour de la pointe, relevé jour par jour (Excel).",
    parAnnee: true,
  },
  {
    type: "fec",
    titre: "FEC",
    description:
      "Fichier des Écritures Comptables (format A47 A-1) sur l'année : uniquement les mois clôturés.",
    parAnnee: true,
  },
];

function ExportCard({ definition }: { definition: ExportDef }) {
  const toast = useToast();
  const { genererExport } = useExportsComptables();
  const [periode, setPeriode] = useState<Periode>(periodeCourante());
  const [isGenerating, setIsGenerating] = useState(false);

  async function generer(setSignedUrl: (url: string) => void) {
    setIsGenerating(true);
    try {
      const payload = definition.parAnnee
        ? { year: periode.year }
        : { year: periode.year, month: periode.month };
      const url = await genererExport(definition.type, {
        ...payload,
        ...definition.payload,
      });
      setSignedUrl(url);
    } catch (error) {
      toast.error({
        title: "Export impossible",
        description: handleAPIError(error),
      });
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <Card className={styles.exportCard}>
      <Title variant="hecto">{definition.titre}</Title>
      <Text>{definition.description}</Text>
      <div className={styles.exportActions}>
        {definition.parAnnee ? (
          <AnneePicker
            label="Année"
            value={periode.year}
            onChange={(year) => setPeriode({ year, month: 12 })}
          />
        ) : (
          <MoisPicker
            label="Jusqu'à fin"
            value={periode}
            onChange={setPeriode}
          />
        )}
        <DocumentDownloader>
          {(setSignedUrl: (url: string) => void) => (
            <Button
              icon={IconDownload}
              isLoading={isGenerating}
              onClick={() => generer(setSignedUrl)}
              className={styles.downloadButton}
            >
              Télécharger
            </Button>
          )}
        </DocumentDownloader>
      </div>
    </Card>
  );
}

export default function ExportsTab() {
  return (
    <div className={styles.stack}>
      <Callout variant="info">
        Les exports Excel reflètent la projection courante (périodes ouvertes
        incluses). Le FEC, lui, n'exporte que les mois clôturés : c'est le
        fichier à transmettre à votre expert-comptable ou en cas de contrôle.
      </Callout>
      <div className={styles.cardsRow}>
        {EXPORTS.map((definition) => (
          <ExportCard key={definition.titre} definition={definition} />
        ))}
      </div>
    </div>
  );
}
